import { Activity, BellDot } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReadingCode } from "@/lib/api";
import { useTodayVitalsContext } from "@/context/TodayVitalsContext";
import { flagFor, flagTone, fmtNum, latestRecordedAt, recordedByLine } from "@/lib/vitals";
import { RX_VITALS_SECTION_ID } from "@/components/PrescriptionFormSections/VitalsForm";

interface Props {
    ageMonths: number | null;
    /** Prescription open: point to newer vitals waiting in the form */
    prescribing: boolean;
}

const ITEMS: { code: "bp" | Exclude<ReadingCode, "bp_systolic" | "bp_diastolic" | "height">; label: string; unit: string }[] = [
    { code: "bp", label: "BP", unit: "" },
    { code: "pulse", label: "Pulse", unit: "" },
    { code: "temp", label: "T", unit: "°F" },
    { code: "spo2", label: "SpO2", unit: "%" },
    { code: "weight", label: "Wt", unit: "kg" },
    { code: "rbs", label: "RBS", unit: "" },
];

/**
 * Today's vitals in the patient header. Shown only once something is recorded today: until then the
 * prescription's Vitals section is where they go (previous values under each field), and the Vitals
 * tab / front desk record them. While the doctor prescribes, the Vitals section already shows today's
 * values, so the strip stays out of the way and appears only to point at a newer set recorded meanwhile.
 */
export default function VitalsStrip({ ageMonths, prescribing }: Props) {
    const ctx = useTodayVitalsContext();
    if (!ctx?.data) return null;
    const { today } = ctx.data;

    const items = ITEMS.map(item => {
        if (item.code === "bp") {
            const s = today.bp_systolic, d = today.bp_diastolic;
            return s && d ? { ...item, text: `${fmtNum(s.value)}/${fmtNum(d.value)}`, flag: flagFor(s, ageMonths) ?? flagFor(d, ageMonths) } : null;
        }
        const r = today[item.code];
        return r ? { ...item, text: `${fmtNum(r.value)}${item.unit ? ` ${item.unit}` : ""}`, flag: flagFor(r, ageMonths) } : null;
    }).filter(Boolean) as { code: string; label: string; text: string; flag: "H" | "L" | null }[];

    if (!items.length) return null;

    const newest = latestRecordedAt(today);
    const newestReading = Object.values(today).find(r => r?.recordedAt === newest);
    const pending = prescribing && !!newest && ctx.appliedAt !== null && newest !== ctx.appliedAt;
    if (prescribing && !pending) return null;

    return (
        <div className="flex items-center gap-x-3 gap-y-1 px-4 py-1 border-t border-border/60 bg-muted/20 text-xs overflow-x-auto whitespace-nowrap">
            <span className="flex items-center gap-1 flex-shrink-0 font-semibold text-muted-foreground text-2xs">
                <Activity className="w-3 h-3" /> Today
            </span>

            {/* First in the row so it is never scrolled out of view */}
            {pending && (
                <button
                    type="button"
                    onClick={() => document.getElementById(RX_VITALS_SECTION_ID)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="flex flex-shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-semibold text-primary hover:bg-primary/15"
                >
                    <BellDot className="w-3 h-3" /> New vitals · review
                </button>
            )}

            {!prescribing && items.map(item => (
                <span key={item.code} className="flex items-baseline gap-1 flex-shrink-0">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className={cn("font-semibold tabular-nums text-foreground", flagTone(item.flag, item.code))}>
                        {item.text}
                    </span>
                    {item.flag && (
                        <span className={cn("text-2xs font-bold", flagTone(item.flag, item.code))}>{item.flag}</span>
                    )}
                </span>
            ))}
            {!prescribing && newestReading && (
                <span className="text-2xs text-muted-foreground flex-shrink-0">· {recordedByLine(newestReading)}</span>
            )}
        </div>
    );
}
